import type { EditingProject } from "../types/project";
import { fileFingerprint } from "../storage/fingerprint";
import { loadWaveform, saveWaveform } from "../storage/database";
import type {
  WaveformPyramid,
  WaveformWorkerMessage,
  WaveformWorkerRequest,
  WaveformWorkerResponse,
} from "./types";

type Pending = {
  resolve: (result: { pyramid: WaveformPyramid; elapsedMs: number }) => void;
  reject: (error: Error) => void;
  onProgress?: (pyramid: WaveformPyramid) => void;
};

export class WaveformRequestSupersededError extends Error {
  constructor() {
    super("Waveform request superseded");
    this.name = "WaveformRequestSupersededError";
  }
}

export function isWaveformRequestSuperseded(
  error: unknown,
): error is WaveformRequestSupersededError {
  return error instanceof WaveformRequestSupersededError;
}

export class WaveformService {
  private readonly overviewWorker = this.createWorker();
  private detailWorker = this.createWorker();
  private activeDetailId: string | null = null;
  private interactivePlayback = false;
  private overviewPaused = false;
  private readonly pending = new Map<string, Pending>();

  async analyzeOverview(
    file: File,
    project: EditingProject,
    onProgress: (pyramid: WaveformPyramid) => void,
  ): Promise<{ pyramid: WaveformPyramid; elapsedMs: number; cached: boolean }> {
    const key = fileFingerprint(
      file,
      project.sampleRate,
      project.durationFrames,
    );
    const cached = await loadWaveform(key).catch(() => null);
    if (cached?.complete) {
      onProgress(cached);
      return { pyramid: cached, elapsedMs: 0, cached: true };
    }
    const result = await this.request(
      this.overviewWorker,
      {
        id: crypto.randomUUID(),
        type: "overview",
        file,
        sampleRate: project.sampleRate,
        durationFrames: project.durationFrames,
        trackStartSeconds: project.trackStartSeconds,
      },
      onProgress,
    );
    await saveWaveform(key, result.pyramid).catch(() => undefined);
    return { ...result, cached: false };
  }

  analyzeDetail(
    file: File,
    project: EditingProject,
    startFrame: number,
    endFrame: number,
    bucketCount = 1600,
  ): Promise<{ pyramid: WaveformPyramid; elapsedMs: number }> {
    if (this.activeDetailId) {
      const previous = this.pending.get(this.activeDetailId);
      this.pending.delete(this.activeDetailId);
      previous?.reject(new WaveformRequestSupersededError());
      this.detailWorker.terminate();
      this.detailWorker = this.createWorker();
    }
    const id = crypto.randomUUID();
    this.activeDetailId = id;
    this.updateOverviewPause();
    return this.request(this.detailWorker, {
      id,
      type: "detail",
      file,
      sampleRate: project.sampleRate,
      durationFrames: project.durationFrames,
      trackStartSeconds: project.trackStartSeconds,
      startFrame,
      endFrame,
      bucketCount,
    }).finally(() => {
      if (this.activeDetailId === id) {
        this.activeDetailId = null;
        this.updateOverviewPause();
      }
    });
  }

  setInteractivePlayback(active: boolean): void {
    if (this.interactivePlayback === active) return;
    this.interactivePlayback = active;
    this.updateOverviewPause();
  }

  dispose(): void {
    this.overviewWorker.terminate();
    this.detailWorker.terminate();
    for (const pending of this.pending.values())
      pending.reject(new Error("Waveform service disposed"));
    this.pending.clear();
    this.activeDetailId = null;
    this.interactivePlayback = false;
    this.overviewPaused = false;
  }

  private createWorker(): Worker {
    const worker = new Worker(
      new URL("./waveform.worker.ts", import.meta.url),
      { type: "module" },
    );
    worker.onmessage = (event: MessageEvent<WaveformWorkerResponse>) => {
      const response = event.data;
      const pending = this.pending.get(response.id);
      if (!pending) return;
      if (response.type === "progress") pending.onProgress?.(response.pyramid);
      if (response.type === "error") {
        this.pending.delete(response.id);
        pending.reject(new Error(response.message));
      }
      if (response.type === "complete") {
        this.pending.delete(response.id);
        pending.resolve({
          pyramid: response.pyramid,
          elapsedMs: response.elapsedMs,
        });
      }
    };
    return worker;
  }

  private request(
    worker: Worker,
    request: WaveformWorkerRequest,
    onProgress?: (pyramid: WaveformPyramid) => void,
  ): Promise<{ pyramid: WaveformPyramid; elapsedMs: number }> {
    return new Promise((resolve, reject) => {
      this.pending.set(request.id, { resolve, reject, onProgress });
      worker.postMessage(request);
    });
  }

  private updateOverviewPause(): void {
    const paused = this.interactivePlayback || this.activeDetailId !== null;
    if (paused === this.overviewPaused) return;
    this.overviewPaused = paused;
    const message: WaveformWorkerMessage = {
      type: "set-overview-paused",
      paused,
    };
    this.overviewWorker.postMessage(message);
  }
}
